'use client';

import { useEffect } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import {
  isAuthenticated,
  getUserRole,
  canAccessRoute,
  getDefaultRedirect,
  ROLES,
  type Role,
} from '@/lib/auth';
import RoleChangeRedirect from '@/components/RoleChangeRedirect';

interface RouteProtectionProps {
  children: React.ReactNode;
}

const PUBLIC_ROUTES = ['/login', '/verify-otp', '/'];

function isPublic(pathname: string) {
  return PUBLIC_ROUTES.some((r) => pathname === r || pathname.startsWith(r + '/'));
}

export default function RouteProtection({ children }: RouteProtectionProps) {
  const router = useRouter();
  const pathname = usePathname();

  // Fire-and-forget redirect — synchronous checks run in effect before first paint
  useEffect(() => {
    if (!pathname || isPublic(pathname)) {
      if (pathname && isAuthenticated() && ['/login', '/verify-otp'].includes(pathname)) {
        router.replace(getDefaultRedirect(getUserRole()));
      }
      return;
    }
    if (!isAuthenticated()) {
      router.replace(`/login?redirect=${encodeURIComponent(pathname)}`);
      return;
    }
    if (!canAccessRoute(pathname)) {
      router.replace(getDefaultRedirect(getUserRole()));
    }
  }, [pathname, router]);

  // Render children immediately — no loading screen.
  // If the user is unauthorized, the redirect above fires before they see the page.
  return (
    <>
      <RoleChangeRedirect />
      {children}
    </>
  );
}

// ─── HOC and gate helpers (unchanged) ────────────────────────────────────────

export function withRoleProtection(Component: React.ComponentType, allowedRoles: Role[]) {
  return function ProtectedComponent(props: any) {
    const router = useRouter();
    const pathname = usePathname();

    useEffect(() => {
      if (!pathname) return;
      const userRole = getUserRole();
      if (!isAuthenticated()) {
        router.replace(`/login?redirect=${encodeURIComponent(pathname)}`);
        return;
      }
      if (!userRole || !allowedRoles.includes(userRole)) {
        router.replace(getDefaultRedirect(userRole));
      }
    }, [pathname, router]);

    return <Component {...props} />;
  };
}

export function RoleGate({
  children,
  allowedRoles,
  fallback,
}: {
  children: React.ReactNode;
  allowedRoles: Role[];
  fallback?: React.ReactNode;
}) {
  const userRole = getUserRole();
  if (!userRole || !allowedRoles.includes(userRole)) return <>{fallback || null}</>;
  return <>{children}</>;
}

export function AuthGate({
  children,
  fallback,
}: {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}) {
  if (!isAuthenticated()) return <>{fallback || null}</>;
  return <>{children}</>;
}
